import React from 'react';
import { CalendarClock, CalendarOff, HelpCircle } from 'lucide-react';
import { GEWERKE, type GewerkId } from './gewerkeKatalog';
import { gewerkeMitLuecke, type AusgeschlossenesGewerk, type GewerkAusPlan } from './koordinierungLogic';

/**
 * Die Fehlstellen des Plans, sichtbar gemacht — reine Anzeige.
 *
 * Drei Listen, die aus der Analyse herausfallen und deshalb GENAU HIER
 * hingehoeren:
 *   1. Gewerke, die der Katalog nicht kennt (Zuordnung anbieten),
 *   2. Gewerke ohne verwertbaren Zeitraum (nur benennen — der Zeitraum
 *      gehoert in den SiGe-Plan und wird nicht hier erfunden),
 *   3. Gewerke, die zwar gerechnet werden, aber undatierte Arbeitsschritte
 *      tragen (`gewerkeMitLuecke`).
 *
 * Ein Gewerk stillschweigend wegzulassen waere der gefaehrlichere Fehler: die
 * Matrix saehe vollstaendig aus, obwohl das Gewerk mit dem groessten
 * Kollisionsrisiko schlicht kein Datum hatte. Fuer Liste 3 gilt das doppelt —
 * dort fehlt nicht das Gewerk, sondern nur ein Teil seiner Zeit, und es sieht
 * dabei vollstaendig aus.
 */

interface Props {
    /** Alle Gewerke des Plans, auch die zugeordneten. */
    gewerke: GewerkAusPlan[];
    offeneGewerke: GewerkAusPlan[];
    ohneZeitraum: AusgeschlossenesGewerk[];
    zuordnungen: Record<string, GewerkId>;
    isReadOnly: boolean;
    onZuordnen: (norm: string, gewerk: GewerkId | null) => void;
}

const tagText = (iso: string | null): string =>
    typeof iso === 'string' && iso.length >= 10
        ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`
        : '—';

export const GewerkZuordnung: React.FC<Props> = ({
    gewerke, offeneGewerke, ohneZeitraum, zuordnungen, isReadOnly, onZuordnen,
}) => {
    // Gewerke, die gerechnet werden, deren Zeitraum aber unvollstaendig ist.
    // Sie stehen in KEINER der beiden anderen Listen — ihre Hülle ist ja gültig.
    const mitLuecke = gewerkeMitLuecke(gewerke);

    return (
    <div className="space-y-4">

        {/* ─── Nicht zugeordnete Gewerke ──────────────────────────────── */}
        {offeneGewerke.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex items-start gap-2 mb-1">
                    <HelpCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm font-black text-slate-800">
                        {offeneGewerke.length} Gewerk(e) ohne Zuordnung
                    </p>
                </div>
                <p className="text-xs text-slate-500 mb-4 ml-6">
                    Der Katalog konnte diese Schreibweisen keinem Gewerk zuordnen. Sie bleiben in der
                    Analyse und in der Matrix — es fehlen ihnen nur die Regelvorschläge. Eine Zuordnung
                    gilt für diese Sitzung und entscheidet ausschließlich darüber, welche Regeln
                    vorgeschlagen werden; was Sie daraus bestätigen, wird gespeichert.
                </p>

                <div className="space-y-2">
                    {offeneGewerke.map(g => (
                        <div key={g.norm} className="flex items-start gap-3 flex-wrap border-t border-slate-100 pt-2.5">
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-slate-800">{g.label}</p>
                                <p className="text-[11px] text-slate-400">
                                    {g.anzahlSchritte} Arbeitsschritt(e)
                                    {g.von && g.bis && ` · ${tagText(g.von)} – ${tagText(g.bis)}`}
                                    {g.schreibweisen.length > 1 && ` · weitere Schreibweisen: ${g.schreibweisen.slice(1).join(', ')}`}
                                </p>
                            </div>
                            <select
                                value={zuordnungen[g.norm] || ''}
                                disabled={isReadOnly}
                                onChange={e => onZuordnen(g.norm, (e.target.value as GewerkId) || null)}
                                className="text-xs font-bold border border-slate-300 rounded-lg px-2 py-1.5 bg-white disabled:opacity-40 max-w-full"
                            >
                                <option value="">— nicht zugeordnet —</option>
                                {GEWERKE.map(k => (
                                    <option key={k.id} value={k.id}>{k.bezeichnung}</option>
                                ))}
                            </select>
                        </div>
                    ))}
                </div>

                {/* Warum kein automatischer Vorschlag: eine Levenshtein- oder
                    Trigramm-Nähe verwechselt „Estrich" und „Elektro" oder
                    „Maler" und „Maurer" gelegentlich. Ein nicht erkanntes
                    Gewerk fällt sofort auf; eine falsche Zuordnung fällt
                    niemandem auf, weil sie plausibel aussieht. */}
                <p className="text-[11px] text-slate-400 mt-4 pt-3 border-t border-slate-100">
                    Es wird bewusst kein ähnlicher Name vorgeschlagen: eine unscharfe Zuordnung
                    verwechselt „Estrich" und „Elektro" oder „Maler" und „Maurer" gelegentlich — und eine
                    falsche Zuordnung fällt niemandem auf, weil sie plausibel aussieht.
                </p>
            </div>
        )}

        {/* ─── Ohne Zeitraum ──────────────────────────────────────────── */}
        {ohneZeitraum.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex items-start gap-2 mb-1">
                    <CalendarOff className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm font-black text-slate-800">
                        {ohneZeitraum.length} Gewerk(e) ohne verwertbaren Zeitraum
                    </p>
                </div>
                <p className="text-xs text-slate-500 mb-4 ml-6">
                    Sie nehmen an der Überschneidungsprüfung nicht teil. Der Zeitraum gehört an den
                    Arbeitsschritt im SiGe-Plan und wird hier nicht ersetzt — ein erfundener Zeitraum
                    erzeugt eine erfundene Überschneidung, und die stünde als Feststellung im Plan.
                </p>
                <ul className="space-y-2">
                    {ohneZeitraum.map(a => (
                        <li key={a.gewerk.norm} className="border-t border-slate-100 pt-2.5">
                            <p className="text-sm font-bold text-slate-800">{a.gewerk.label}</p>
                            <p className="text-xs text-slate-500">{a.hinweis}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5">
                                {a.gewerk.anzahlSchritte} Arbeitsschritt(e)
                                {a.gewerk.schritteOhneDatum > 0 && `, davon ${a.gewerk.schritteOhneDatum} ohne Datum`}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>
        )}

        {/* ─── Arbeitsschritte ohne Datum ─────────────────────────────── */}
        {/* Diese Gewerke stehen in der Matrix und sehen dort vollständig aus.
            Gerechnet wurde aber nur die Hülle ihrer datierten Schritte: ein
            Schritt „ab 20.09., Ende offen" zieht sie nicht auf und fällt in
            keine Ausschussliste. Ohne diesen Kasten erschiene eine echte
            Überschneidung als geprüft und unbedenklich. */}
        {mitLuecke.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex items-start gap-2 mb-1">
                    <CalendarClock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm font-black text-slate-800">
                        {mitLuecke.length} Gewerk(e) mit Arbeitsschritten ohne Datum
                    </p>
                </div>
                <p className="text-xs text-slate-500 mb-4 ml-6">
                    Diese Gewerke nehmen an der Prüfung teil — aber nur mit ihren datierten
                    Arbeitsschritten. Der gerechnete Zeitraum umfasst die übrigen NICHT, und eine leere
                    Matrixzelle ist hier deshalb kein Nachweis der Gefährdungsfreiheit.
                </p>
                <ul className="space-y-2">
                    {mitLuecke.map(g => (
                        <li key={g.norm} className="border-t border-slate-100 pt-2.5">
                            <p className="text-sm font-bold text-slate-800">{g.label}</p>
                            <p className="text-xs text-slate-500">
                                {g.schritteOhneDatum} von {g.anzahlSchritte} Arbeitsschritt(en) ohne
                                verwertbaren Zeitraum
                            </p>
                            <p className="text-[11px] text-slate-400 mt-0.5">
                                Gerechnet wird {tagText(g.von)} – {tagText(g.bis)}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>
        )}

        {/* ─── Alles in Ordnung ───────────────────────────────────────── */}
        {/* Die Entwarnung darf NICHT erscheinen, solange irgendein Arbeitsschritt
            ohne Datum ist: sie spräche sonst jedes Gewerk frei, obwohl ein Teil
            des Plans gar nicht geprüft wurde. */}
        {offeneGewerke.length === 0 && ohneZeitraum.length === 0 && mitLuecke.length === 0
            && gewerke.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3 text-sm text-emerald-800">
                Alle {gewerke.length} Gewerke sind zugeordnet und haben einen vollständigen Zeitraum.
            </div>
        )}

        {/* ─── Gewerke des Plans ──────────────────────────────────────── */}
        {gewerke.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide mb-3">
                    Gewerke im Plan ({gewerke.length})
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-slate-400">
                                <th className="font-bold py-1 pr-3">Gewerk</th>
                                <th className="font-bold py-1 pr-3">Zeitraum</th>
                                <th className="font-bold py-1 pr-3">Schritte</th>
                                <th className="font-bold py-1">Räumliche Zuordnung (Freitext)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {gewerke.map(g => (
                                <tr key={g.norm}>
                                    <td className="py-1.5 pr-3 font-bold text-slate-800">
                                        {g.label}
                                        {!g.zugeordnet && <span className="text-amber-500 ml-1" title="nicht zugeordnet">?</span>}
                                        {g.vonHandZugeordnet && <span className="text-blue-500 ml-1" title="von Hand zugeordnet">·</span>}
                                    </td>
                                    <td className="py-1.5 pr-3 text-slate-600 whitespace-nowrap">
                                        {g.von || g.bis ? `${tagText(g.von)} – ${tagText(g.bis)}` : '—'}
                                    </td>
                                    {/* Die Zahl der undatierten Schritte steht an JEDEM Gewerk —
                                        ohne sie sieht ein halb terminiertes Gewerk in dieser
                                        Tabelle aus wie ein vollständig geplantes. */}
                                    <td className="py-1.5 pr-3 text-slate-600">
                                        {g.anzahlSchritte}
                                        {g.schritteOhneDatum > 0 && (
                                            <span
                                                className="text-amber-600 font-bold ml-1"
                                                title={`${g.schritteOhneDatum} Arbeitsschritt(e) ohne verwertbaren Zeitraum — sie stecken NICHT im gerechneten Zeitraum.`}
                                            >
                                                ({g.schritteOhneDatum} ohne Datum)
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-1.5 text-slate-500">{g.bereiche.join(' · ') || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )}
    </div>
    );
};

